import {
  ARCHIVES_PAGE_TYPE,
  CONTRIBUTOR_PAGE_TYPE,
  PRODUCT_LIST_PAGE_TYPE,
  SEARCH_PAGE_TYPE,
  STATIC_PAGE_TYPE,
  PRODUCTION_FACEBOOK_APP_ID,
  DEVELOPMENT_FACEBOOK_APP_ID,
  SOLD_CONTENT_TYPES,
  EDITION_ID_EN_US,
  EDITION_ID_EN_GB,
  VIDEO_SERIES_PAGE_TYPE,
  EDITION_PINTEREST_VERIFY
} from '../constants';
import React from 'react';
import Helmet from 'react-helmet';
import { injectIntl, defineMessages } from 'react-intl';
import { decorateIntlMessages } from './common/rosetta_formatted_message';
import { onProductionHostname } from '../utils/env';
import { formatArchivesMessage, nextAndPrevArchivesPaths } from '../utils/date';

import {get, map, nth, find, filter, entries, flatMap, isEmpty, isFinite, isInteger} from 'lodash';
import PropTypes from 'prop-types';
import { stringify } from 'querystring';
import { onHomepage } from '../utils/dom';
import padPrice from '../utils/padPrice';
import {
  forceHTTPS,
  fullURLofPath,
  getAbsolutePath
} from '../utils/url';
import { isYoutube } from '../utils/widgetInspector';
import { StaticAssets } from './common/static_assets';

const RELATED_PRODUCTS_MODULE_TYPE = 'related_products';

export const i18nMessages = decorateIntlMessages(__filename, defineMessages({
  archives_title: {
    id: 'archives_title',
    description: 'Title for archives pages',
    defaultMessage: 'Refinery29 Archives - {date}'
  },
  archives_meta_title: {
    id: 'archives_meta_title',
    description: 'Meta title for archives pages',
    defaultMessage: 'Refinery29 - {date}'
  },
  archives_meta_description: {
    id: 'archives_meta_description',
    description: 'Meta description for archives pages',
    defaultMessage: 'Dig into the {date} R29 archives and find all your favorite stories in one place!'
  },
  contributor_title: {
    id: 'contributor_title',
    description: 'Title for contributor pages',
    defaultMessage: '{contributor}\'s Recent Articles - Refinery29'
  },
  contributor_meta_title: {
    id: 'contributor_meta_title',
    description: 'Meta title for contributor pages',
    defaultMessage: '{contributor}\'s Recent Articles - Refinery29'
  },
  contributor_meta_description: {
    id: 'contributor_meta_description',
    description: 'Meta description for contributor pages',
    defaultMessage: 'Get all the latest from {contributor} for how to live a stylish, well-rounded life.'
  },
  paid_description: {
    id: 'paid_description',
    description: 'Share description with the translation for "Paid Content" for social sharing on sponsored stories',
    defaultMessage: '(Paid Content) {shareDescription}'
  }
}));

const formatContributorMessage =
  (message, modules, intl) =>
    intl.formatMessage(message, { contributor: get(modules, '[0].title') });

export const Head = (props, context) => (
  <Helmet
    title={getTitleFromProps(props)}
    meta={getMetaTagsFromProps(props, context)}
    link={getLinkTagsFromProps(props, context)}
    script={getScriptTagsFromProps(props, context)}
    encodeSpecialCharacters={false}
  />
);

Head.propTypes = {
  ad_data: PropTypes.object.isRequired,
  alternate_urls: PropTypes.arrayOf(PropTypes.object),
  analytics: PropTypes.shape({
    page_type: PropTypes.string.isRequired,
    url_full: PropTypes.string.isRequired
  }).isRequired,
  edition_data: PropTypes.shape({
    id: PropTypes.number,
    domain: PropTypes.string,
    config: PropTypes.object
  }).isRequired,
  meta_data: PropTypes.shape({
    meta_title: PropTypes.string,
    meta_description: PropTypes.string,
    meta_noindex: PropTypes.boolean,
    social_title: PropTypes.string
  }).isRequired,
  modules: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    rows: PropTypes.arrayOf(PropTypes.shape({
      cards: PropTypes.array
    }))
  }).isRequired).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired
  }).isRequired,
  archives: PropTypes.shape({
    timestamp: PropTypes.number,
    year: PropTypes.number,
    month: PropTypes.number,
    day: PropTypes.number
  }),
  article: PropTypes.object,
  product: PropTypes.object,
  productList: PropTypes.shape({
    heroImage: PropTypes.string,
    brand: PropTypes.object,
    retailer: PropTypes.object
  })
};

Head.defaultProps = {
  analytics: {},
  meta_data: {},
  article: {},
  product: {},
  productList: {}
};

Head.contextTypes = {
  location: PropTypes.object
};

export default injectIntl(Head);

function getTitleFromProps(props) {
  const { analytics, archives, article, intl, meta_data, modules } = props;

  if (analytics.page_type === ARCHIVES_PAGE_TYPE && archives.timestamp) {
    return formatArchivesMessage(
      i18nMessages.archives_title,
      archives,
      intl
    );
  }

  if (analytics.page_type === CONTRIBUTOR_PAGE_TYPE) {
    return formatContributorMessage(
      i18nMessages.contributor_title,
      modules,
      intl
    );
  }

  return get(meta_data, 'meta_title')
    || get(article, 'sanitizedTitle')
    || 'Refinery29';
}

export function getImageDims(image_asset) {
  let imageWidth = 0;
  let imageHeight = 0;

  let crop = image_asset.replace(/.+\/(\d+,\d+,\d+,\d+)\/.+/, '$1'); // eslint-disable-line no-useless-escape
  if (crop !== image_asset) {
    crop = crop.split(',');
    if (crop.length === 4) {
      imageWidth = crop[2];
      imageHeight = crop[3];
    }
  }

  let size = image_asset.replace(/.+[\/_](\d+x\d+)[,\.].+/, '$1'); // eslint-disable-line no-useless-escape
  if (size !== image_asset && imageWidth === 0 && imageHeight === 0) {
    size = size.split('x');
    if (size.length === 2) {
      imageWidth = size[0];
      imageHeight = size[1];
    }
  }
  return { imageWidth, imageHeight };
}

function replaceDoubleQuotes(meta_string) {
  if (meta_string) {
    meta_string = meta_string.replace(/"/g, '&quot;');
  }
  return meta_string;
}

function getMetaTagsFromProps(props, { location }) {
  const {
    ad_data, analytics, article, intl, meta_data, edition_data, product,
    productList
  } = props;
  const { meta_title, meta_description, meta_noindex, sailthru } = meta_data;
  const { excerpt_truncated_325, dek, meta, title } = article;
  const { facebook_page, twitter_handle } = get(edition_data, 'config', {});
  const fb_opener = find(article.opener_images, { label: 'FACEBOOK' });
  const pinterest_opener = find(article.opener_images, { label: 'PINTEREST' });
  const product_opener = get(product, 'images[0].url');
  const isCategoryList = get(productList, 'heroImage') || !isEmpty(get(productList, 'openerImages'));
  const sold_content_type = meta ? meta.sold_content_type : null;
  const R29_LOGO_JPG = forceHTTPS(fullURLofPath(StaticAssets.r29LogoOg, location));

  let pinterest_image_asset;
  let image_asset;

  if (product_opener) {
    pinterest_image_asset = image_asset = product_opener;
  } else if (isCategoryList) {
    const heroImage = get(productList, 'heroImage');
    const pinterestOpener = find(productList.openerImages, { type: 'PINTEREST' });
    const facebookOpener = find(productList.openerImages, { type: 'FACEBOOK' });

    pinterest_image_asset = heroImage || (isEmpty(pinterestOpener) ? R29_LOGO_JPG : pinterestOpener.asset.src.full);
    image_asset = heroImage || (isEmpty(facebookOpener) ? R29_LOGO_JPG : facebookOpener.asset.src.full);
  } else {
    pinterest_image_asset = isEmpty(pinterest_opener) ? R29_LOGO_JPG : pinterest_opener.asset.src.full;
    image_asset = isEmpty(fb_opener) ? R29_LOGO_JPG : fb_opener.asset.src.full;
  }

  let ogType = !isEmpty(article) ? 'article' : 'website';
  const news_keywords = get(article, 'meta.news_keywords');
  const socialTitle = get(article, 'meta.social_title');
  const url_full = get(analytics, 'url_full', '');
  const httpsPinterestImageAsset = forceHTTPS(pinterest_image_asset);
  const httpsImageAsset = forceHTTPS(image_asset);
  const { imageWidth, imageHeight } = getImageDims(image_asset);

  const pageTitle = replaceDoubleQuotes(getMetaTitle({
    ...props,
    title: meta_title || title
  }));
  const pageSocialTitle = replaceDoubleQuotes(getMetaTitle({
    ...props,
    title: socialTitle || meta_title || title
  }));
  const description = replaceDoubleQuotes(getMetaDescription({
    ...props,
    dek,
    excerpt_truncated_325,
    meta_description
  }));

  const socialDescription = sold_content_type === SOLD_CONTENT_TYPES.CUSTOM
    ? replaceDoubleQuotes(intl.formatMessage(i18nMessages.paid_description, { shareDescription: description }))
    : description;

  const fbAppId =
    onProductionHostname(location.hostname)
      ? PRODUCTION_FACEBOOK_APP_ID
      : DEVELOPMENT_FACEBOOK_APP_ID;

  const options = [
    {
      content: 'ie=edge',
      'http-equiv': 'x-ua-compatible'
    },
    {
      name: 'title',
      content: pageTitle,
      itemprop: 'title'
    },
    {
      name: 'description',
      content: description,
      itemprop: 'description'
    },
    {
      property: 'og:url',
      content: url_full
    },
    {
      name: 'twitter:title',
      content: pageSocialTitle
    },
    {
      property: 'og:title',
      content: pageSocialTitle,
      itemprop: 'name'
    },
    {
      property: 'og:description',
      content: socialDescription
    },
    {
      property: 'og:image',
      content: httpsImageAsset,
      itemprop: 'image'
    },
    {
      property: 'og:image:secure_url',
      content: httpsImageAsset,
      itemprop: 'image'
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image'
    },
    {
      name: 'twitter:site',
      content: `@${twitter_handle}`
    },
    {
      name: 'twitter:description',
      content: socialDescription
    },
    {
      name: 'twitter:image',
      content: httpsImageAsset
    },
    {
      name: 'pinterest:image',
      content: httpsPinterestImageAsset
    },
    {
      property: 'fb:app_id',
      content: fbAppId
    },
    {
      property: 'fb:pages',
      //Refinery29,Entertainment,RSVP,Beauty,UK,Wellness,Strong Opinions,Short Cuts,DE,FR
      content: '86973707921,587074007996399,269626263209765,509115739117952,1562713254009921,583745561686633,656112191237463,284423314989162,974273182666651,192477361634120'
    },
    {
      property: 'article:publisher',
      content: facebook_page
    },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1'
    },
    {
      name: 'theme-color',
      content: '#fd5b4e'
    }
  ];

  if (imageWidth > 0 && imageHeight > 0) {
    options.push({
      property: 'og:image:width',
      content: imageWidth
    });
    options.push({
      property: 'og:image:height',
      content: imageHeight
    });
  }

  if (analytics.page_type === VIDEO_SERIES_PAGE_TYPE) {
    const sections = getAllSections(article);

    let ogVideoUrl;
    let ogVideoType;
    sections.forEach((section: Object): void => {
      if (section.type === 'video:internal') {
        ogVideoUrl = `https://content.jwplatform.com/videos/${section.content.jw_video_id}-720.mp4`;
        ogVideoType = 'video/mp4';
        return false;
      } else if (section.type === 'video:external' || (section.type === 'widget:html' && isYoutube(section.content.resource))) {
        ogVideoUrl = naivelyExtractSrcFromIframeEmbed(section.content.resource);
        ogVideoType = 'text/html';
        return false;
      }
    });

    if (ogVideoUrl) {
      ogType = 'video';

      options.push({
        property: 'og:video:type',
        content: ogVideoType
      });
      options.push({
        property: 'og:video',
        content: ogVideoUrl
      });
      options.push({
        property: 'og:video:secure_url',
        content: ogVideoUrl
      });
      options.push({
        property: 'og:video:width',
        content: '1280'
      });
      options.push({
        property: 'og:video:height',
        content: '720'
      });
    }
  }

  options.push({
    property: 'og:type',
    content: ogType
  });

  if (!isEmpty(news_keywords)) {
    options.push({
      name: 'news_keywords',
      content: news_keywords
    });
  }

  if (EDITION_PINTEREST_VERIFY[edition_data.id]) {
    options.push({
      name: 'p:domain_verify',
      content: EDITION_PINTEREST_VERIFY[edition_data.id]
    });
  }

  /*
   * All Sailthru data is pushed up to the content API via publication hooks on
   * our end, and never done via Sailthru's spidering tool. The only reason we
   * keep these meta tags on the page is so that Sailthru can continue to track
   * metrics like pageviews, which then feed their personalization algorithms.
   */
  if (!isEmpty(sailthru)) {
    if (!isEmpty(sailthru.tags)) {
      options.push({
        property: 'sailthru.tags',
        content: sailthru.tags
      });
    }

    if (!isEmpty(sailthru.authors)) {
      options.push({
        property: 'sailthru.author',
        content: sailthru.authors
      });
    }

    options.push({
      property: 'sailthru.title',
      content: socialTitle || meta_title || title
    });
  }

  /*
   * The following tags on articles are for Quantcast:
   * 'entry_template', 'author', and 'channel_tag'
   */
  if (!isEmpty(article)) {
    options.push({
      property: 'entry_template',
      content: analytics.page_type
    });
    if (!isEmpty(article.bylines)) {
      options.push({
        property: 'author',
        content: article.bylines.map(byline => byline.name).join(',')
      });
    }
    (ad_data.ad_section_name || []).forEach(channel => {
      options.push({
        property: 'channel:tag',
        content: channel
      });
    });
  }

  if (meta_noindex) {
    return [
      ...options,
      {
        name: 'robots',
        content: 'noindex'
      }
    ];
  }

  return options;
}

function getLinkTagsFromProps(props, { location }) {
  const {
    alternate_urls, analytics, api, archives, edition_data
  } = props;
  const canonicalURL = getCanonicalURLForPage(props, { location });

  const tags = [
    {
      rel: 'canonical',
      href: canonicalURL
    },
    {
      rel: 'publisher',
      href: 'https://plus.google.com/101601649207969071719'
    },
    {
      rel: 'rich-pin-icon',
      href: forceHTTPS(fullURLofPath(StaticAssets.r29Favicon, location))
    }
  ];

  tags.push(...getPaginationTags({ api }, { location }));

  if (shouldAddRelativeArchiveDateTags({ archives })) {
    tags.push(...getRelativeArchiveDateTags({ api, archives }, { location }));
  }

  if (alternate_urls) {
    let defaultUrl = ''; // An alternate url whose hreflang = 'x-default'

    let defaultEdition = null;
    const currentEdition = edition_data.id;

    const alternateUrlTags = alternate_urls.map(alt_url => {
      const activeEdition = alt_url.edition_id;

      // While looping over alternate URLs, keep an eye out for the one that will be default
      if (thisAlternateShouldBecomeTheDefault(
        activeEdition, defaultEdition, currentEdition
      )) {
        defaultUrl = alt_url.url;
        defaultEdition = activeEdition;
      }

      return {
        rel: 'alternate',
        hreflang: alt_url.lang,
        href: getValidQueryParamUrl(alt_url.url, analytics, api, location)
      };
    });

    const defaultTag = {
      rel: 'alternate',
      hreflang: 'x-default',
      href: getValidQueryParamUrl(defaultUrl, analytics, api, location)
    };

    alternateUrlTags.push(defaultTag);
    return tags.concat(alternateUrlTags);
  }

  return tags;
}

/*
 * Business logic for the rel alternate whose hreflang will be 'x-default':
 * US > UK > current edition > everything else
 */
export function thisAlternateShouldBecomeTheDefault(activeEdition, defaultEdition, currentEdition) {
  switch (activeEdition) {
    case EDITION_ID_EN_US:
      // US is preferred over EVERYTHING
      return true;
    case EDITION_ID_EN_GB:
      // UK is preferred over everything EXCEPT US
      return defaultEdition !== EDITION_ID_EN_US;
    case currentEdition:
      // current site is preferred over everything EXCEPT US and UK
      return (
        defaultEdition !== EDITION_ID_EN_US
        && defaultEdition !== EDITION_ID_EN_GB
      );
    default:
      // anything else is NOT acceptable
      return false;
  }
}

/*
 * The `url` query param that can be passed into this may be
 * the `url_full` from the API. It can also be an `alternate_url`.
 */
function getValidQueryParamUrl(url, analytics, api, location) {
  const { page_type } = analytics;
  const { pagination } = api;

  /*
   *build Urls that only contain whitelisted query paramaters
   * We should pay close attention to this whitelisting logic,
   * since it affects SEO and third-party integrations
   */
  const canonicalParams = {};

  if (
    location.query.page
    /*
     * Include the 'page' param only if it is a paginated entity.
     * We determine whether something is a paginated entity by examining its
     * "pagination" key in the API response. If one of "next" or "prev" is
     * present, it must be a paginated entity. If both are missing or the
     * entire key is missing, it must not be a paginated entity.
     */
    && (get(pagination, 'prev') || get(pagination, 'next'))
  ) {
    canonicalParams.page = location.query.page;
  }

  if (
    location.query.q
    /*
     * The 'q' param is only relevant to search and product list pages,
     * so we should only include it if we're on one of those page types.
     */
    &&
    [PRODUCT_LIST_PAGE_TYPE, SEARCH_PAGE_TYPE].includes(page_type)
  ) {
    canonicalParams.q = location.query.q;
  }

  // Product-list specific query params.
  if (page_type === PRODUCT_LIST_PAGE_TYPE) {
    [
      'sort', 'minPrice', 'maxPrice', 'on_sale',
      'category', 'subcategory', 'class', 'color'
    ]
      .filter(queryParam => queryParam in location.query)
      .forEach(queryParam => {
        canonicalParams[queryParam] = location.query[queryParam];
      });
  }

  if (!isEmpty(canonicalParams)) {
    return url + '?' + stringify(canonicalParams).replace('%20', '+');
    /*
     * Why are we replacing "%20" with "+"?
     * A value with a space can be encoded as either one of those.
     *
     * Sources:
     * https://stackoverflow.com/questions/1634271/url-encoding-the-space-character-or-20
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent
     *
     * stringify goes with '%20'. Let's go with '+' to match what happens
     * when we type a space into the search string on the search page.
     */
  }

  return url;
}

function getCanonicalURLForPage({ analytics, edition_data, api, article }, { location }) {
  if (analytics.page_type === STATIC_PAGE_TYPE || analytics.page_type === ARCHIVES_PAGE_TYPE) {
    /*
     * On static pages we should ignore url_full -- we use one Foundry endpoint
     * for all static pages, so we can't provide the correct url_full.
     * On archive pages, we don't include any date range parameters in url_full.
     *
     * Instead let's just use the existing pathname.
     */
    return 'https://' + edition_data.domain + get(location, 'pathname');
  }

  return get(article, 'meta.rel_canonical_source')
    || getValidQueryParamUrl(analytics.url_full, analytics, api, location);
}

function shouldAddRelativeArchiveDateTags({ archives }) {
  return isFinite(get(archives, 'day'));
}

function getPaginationTags({ api }, { location }) {
  if (!api.pagination) {
    return [];
  }

  const tags = [];
  const { pathname, query, hostname } = location;
  const page = Number(query.page) || 1;
  const { prev, next } = get(api, 'pagination', {});
  const urlWithPath = 'https://' + hostname + pathname + '?';

  if (!isEmpty(prev)) {
    tags.push({
      rel: 'prev',
      href: urlWithPath + stringify({ page: page - 1 })
    });
  }
  if (!isEmpty(next)) {
    tags.push({
      rel: 'next',
      href: urlWithPath + stringify({ page: page + 1 })
    });
  }

  return tags;
}

function getRelativeArchiveDateTags(props, { location }) {
  const {
    edition: { id: editionId } = {},
    archives: { timestamp } = {}
  } = props;
  return map(
    entries(nextAndPrevArchivesPaths(editionId, timestamp)),
    ([rel, path]) => ({ rel, href: 'https://' + location.hostname + path })
  );
}

function getMetaTitle({ analytics, api, archives, intl, modules, title }) {
  if (analytics.page_type === ARCHIVES_PAGE_TYPE && archives.timestamp) {
    return formatArchivesMessage(
      i18nMessages.archives_meta_title,
      archives,
      intl
    );
  }

  if (analytics.page_type === CONTRIBUTOR_PAGE_TYPE) {
    return formatContributorMessage(
      i18nMessages.contributor_meta_title,
      modules,
      intl
    );
  }

  const page = get(api, 'pagination.page', 1);
  return page > 1
    ? `${title} Page ${page}`
    : title;
}

function getMetaDescription(props) {
  const { analytics, archives, dek, excerpt_truncated_325,
    intl, meta_description, modules } = props;

  if (analytics.page_type === ARCHIVES_PAGE_TYPE && archives.timestamp) {
    return formatArchivesMessage(
      i18nMessages.archives_meta_description,
      archives,
      intl
    );
  }

  if (analytics.page_type === CONTRIBUTOR_PAGE_TYPE) {
    return formatContributorMessage(
      i18nMessages.contributor_meta_description,
      modules,
      intl
    );
  }

  return dek || meta_description || excerpt_truncated_325;
}

function getScriptTagsFromProps(props, { location }) {
  const { article, product, modules, productList: { brand, retailer } } = props;

  const schemaJson = getTopLevelSchemaJson(props, { location });

  if (!isEmpty(article)) {
    schemaJson.push(...getSchemaJsonForArticle(props, { location }));
  } else if (!isEmpty(product)) {
    schemaJson.push(...getSchemaJsonForProduct(props));
  }

  const relatedProductModules = modules.filter(({ type }) => type === RELATED_PRODUCTS_MODULE_TYPE);
  if (!isEmpty(relatedProductModules)) {
    schemaJson.push(...getSchemaJsonForRelatedProductModules(relatedProductModules));
  }

  if (get(brand, 'brand_id')) {
    schemaJson.push(...getSchemaJsonForBrand(brand));
  } else if (get(retailer, 'retailer_id')) {
    schemaJson.push(...getSchemaJsonForRetailer(retailer));
  }

  return schemaJson;
}

function getTopLevelSchemaJson({ edition_data }, { location }) {
  const { facebook_page, twitter_handle, pinterest_link,
    instagram_handle, youtube_channel } = get(edition_data, 'config', {});
  const types = onHomepage(location, edition_data)
    ? ['Organization', 'WebPage', 'WebSite']
    : ['WebPage', 'WebSite'];

  return map(types, type => {
    const schema = {
      '@type': type,
      name: 'Refinery29',
      url: getAbsolutePath('', edition_data),
      potentialAction: {
        '@type': 'SearchAction',
        target: getAbsolutePath('/search?q={Refinery29}', edition_data),
        'query-input': 'required name=Refinery29',
        sameAs: [
          `https://www.facebook.com/${facebook_page}`,
          `https://twitter.com/${twitter_handle}`,
          `${pinterest_link}`,
          `https://instagram.com/${instagram_handle}`,
          `https://www.youtube.com/${youtube_channel}`
        ]
      }
    };

    const imageKey = type === 'Organization' ? 'logo' : 'image';
    schema[imageKey] = (
      forceHTTPS(fullURLofPath('/assets/images/r29-logo-b.svg', location))
    );

    return toSchemaJson(schema);
  });
}

function getSchemaJsonForArticle(props, { location }) {
  return [
    getTopLevelSchemaJsonForArticle(props, { location }),
    getBreadcrumbSchemaJsonForArticle(props),
    ...getImageSchemaJsonForArticle(props),
    ...getVideoSchemaJsonForArticle(props),
    ...getAuthorSchemaJsonForArticle(props),
    ...getProductSchemaJsonForArticle(props)
  ];
}

function getSchemaJsonForProduct(props) {
  return [
    getImageSchemaJsonForProduct(props),
    getProductSchemaJsonForProduct(props),
    ...getReviewSchemaJsonForProduct(props)
  ];
}

function getSchemaJsonForBrand(brand) {
  return [
    getOrganizationSchemaJson({
      name: brand.brand_name,
      image: brand.brand_image,
      description: brand.brand_description,
      brand: true,
      url: ''
    })
  ];
}

function getSchemaJsonForRetailer(retailer) {
  return [
    getOrganizationSchemaJson({
      name: retailer.retailer_name,
      image: retailer.retailer_image,
      description: retailer.retailer_description,
      url: retailer.retailer_url
    })
  ];
}

/*
 * For each related product module, return json schema for each product card in each row
 */
function getSchemaJsonForRelatedProductModules(modules) {
  return flatMap(
    modules,
    ({ rows }) => flatMap(
      rows,
      ({ cards }) => map(
        cards, // 'cards' represent products on product detail pages
        product => getProductSchemaJsonForProduct({ product })
      )
    )
  );
}

/*
 * NOTE: We have some redundant data in the JSON here because the following
 * properties are required by the vendor parsely: `url`, `thumbnailUrl`,
 * `creator`, `articleSection`, `keywords`.
 */
function getTopLevelSchemaJsonForArticle({ article, analytics, meta_data }, { location }) {
  const asset_main = get(article, 'asset_main', {});
  const datePublished = toISOString(article.published);
  const dateModified = toISOString(article.published);
  const breadcrumbs = get(article, 'breadcrumbs', []);
  const bylines = get(article, 'bylines', []);

  return toSchemaJson({
    '@type': article.breaking_news ? 'NewsArticle' : 'Article',
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': analytics.url_full
    },
    'headline': article.title,
    'url': analytics.url_full,
    'thumbnailUrl': forceHTTPS(asset_main.src_full),
    'image': {
      '@type': 'ImageObject',
      'url': forceHTTPS(asset_main.src_full),
      'height': asset_main.height,
      'width': asset_main.width
    },
    datePublished,
    dateModified,
    'author': getAuthor(article),
    'creator': bylines.map(byline => byline.name),
    'articleSection': get(article, 'primary_category.name'),
    'keywords': breadcrumbs.map(tag => tag.name),
    'publisher': {
      '@type': 'Organization',
      'name': 'Refinery29',
      'logo': {
        '@type': 'ImageObject',
        'url': forceHTTPS(fullURLofPath('/assets/images/refinery29.png', location)),
        'width': 54,
        'height': 54
      }
    },
    'description': meta_data.meta_description
  });
}

function getImageSchemaJsonForArticle({ article }) {
  const sections = getAllSections(article);
  const imageSections = filter(sections, ({ type } = {}) => /^image/.test(type));
  return map(imageSections, ({ content = {} }) => toSchemaJson({
    '@type': 'ImageObject',
    'author': content.credit,
    'contentUrl': forceHTTPS(content.resource),
    'description': content.description
  }));
}


function isExternalVideo(section) {
  const { content, type } = section;
  if (type === 'widget:html' && content.resource) { // external video saved into new DSE
    return section.content.resource.includes('</iframe>');
  } else if (section.type === 'video:external') { // external video saved into legacy editor
    return true;
  }
  return false;
}

function getVideoSchemaJsonForArticle({ article, meta_data }) {
  const {
    title, original_published, opener_images
  } = article;
  const { meta_title, meta_description } = meta_data;
  const sections = getAllSections(article);
  const openerImage16x9 = get(find(
    opener_images, { type: '16x9' }
  ), 'asset.src.full');

  const internalVideoSections = filter(sections, { type: 'video:internal' });
  const externalVideoSections = filter(sections, isExternalVideo);

  const commonSchema = {
    '@type': 'VideoObject',
    uploadDate: toISOString(original_published)
  };
  return [
    ...map(internalVideoSections, ({ content = {} }) => toSchemaJson({
      ...commonSchema,
      author: getAuthor(article),
      thumbnailUrl: forceHTTPS(openerImage16x9),
      contentUrl: `http://content.jwplatform.com/videos/${content.jw_video_id}-720.mp4`,
      name: meta_title,
      description: meta_description
    })),
    ...map(externalVideoSections, ({ content = {} }) => {
      /*
       * External Videos are not owned by R29 and thus are not made by our author and do not have our upload date or opener thumbnail
       * Unfortunately upload date is a required schema field so we need to default to article date
       * But the name and description should be pulled from the section description if possible
       * And only then defaulting to article content
       */

      const url = naivelyExtractSrcFromIframeEmbed(content.resource) || content.resource;
      let m; let youtubeThumbnail; let pos; let _desc = null;

      // checks if youtube and then retrieves the video id as well
      if ((m = url.match(/https?:\/\/.*\.youtu\.?be[^/]*\/[^?=]+\/([^/\s?#."']+)/)) !== null) {
        if (m.length > 1) { youtubeThumbnail = `https://img.youtube.com/vi/${m[1]}/0.jpg`; }
      }

      // Remove HTML and all extra spaces
      const clean = function(s) { return s.replace(/(<\/?[^>]+>)/g, ' ').replace(/(\s{2,})/g, ' ').trim(); };
      // Check if tag exists in HTML while setting positon to slice later
      const tag = function(HTML, tag) { pos = HTML.indexOf(tag); return pos > 0; };

      // Section Titles have 3 basic formats used by editors
      const desc = content.description;
      const sectionTitle = clean((
      // Do we have a description?
        desc ? (
          // look for an H2, H3, First Line suffixed by BR
          tag.bind(pos)(desc, '</h2>') || tag.bind(pos)(desc, '</h3>') || tag.bind(pos)(desc, '<br>') || tag.bind(pos)(desc, '<br/>'),
          (_desc = clean(desc)),
          // if any exist slice it out or force false to get the default
          (pos > 0 ? desc.slice(0, pos) : title)
        ) :
          title
      // be sure to strip html and extra whitespace
      ) || '');

      // Description will be whatever is left over past the title if it exists
      _desc = _desc || desc || content.description;
      const sectionDescription = clean(
      // Do we have a title captured from above or the default?
        _desc ?
          // strip the title from the description (encode the title so it works in the regex)
          _desc.replace(RegExp('(?:' + sectionTitle.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&') + ')(.+)'), '$1') :
          // otherwise the article title or empty
          title || ''
      // be sure to strip html and extra whitespace
      );

      return toSchemaJson({
        ...commonSchema,
        thumbnailUrl: forceHTTPS(youtubeThumbnail || openerImage16x9),
        embedUrl: forceHTTPS(url),
        description: sectionDescription || '',
        name: sectionTitle || ''
      });
    }
    )
  ];
}

function getProductSchema(name, brand, description, url, image, availability, currency, price, sale_price) {
  availability = availability === false ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock';
  description = !isEmpty(description) ? description : '';

  return toSchemaJson({
    '@type': 'Product',
    'name': name,
    'brand': brand,
    'description': description, // Typically only non-empty for product detail page
    'image': image,
    'url': url,
    'offers': {
      '@type': 'Offer',
      'url': url,
      'availability': availability,
      'priceCurrency': currency,
      'price': padPrice('', sale_price ? sale_price : price).slice(1) // Only show a single price in a single offer; remove leading default US dollar sign
    }
  });
}

function getProductSchemaJsonForArticle({ article }) {
  return map(article.products, (product = {}) => {
    const url = forceHTTPS(product.external_full_path);
    const image = forceHTTPS(product.thumbnail_url);
    const currency = !isEmpty(product.currency) ? product.currency : 'USD'; // Default USD

    return getProductSchema(product.short_title, product.brand_name, product.description, url, image, product.is_sem3_available, currency, product.price, product.special_price);
  });
}

function getAuthorSchemaJsonForArticle({
  article: { bylines },
  edition_data
}) {
  return map(
    filter(bylines, 'featured'),
    ({ name, image, path } = {}) => toSchemaJson({
      '@type': 'Person',
      'name': name,
      'image': image,
      'url': getAbsolutePath(path, edition_data)
    })
  );
}

const leadingSlashRegExp = /^\//;
const stripLeadingSlash =
  (url: string): string =>
    url.replace(leadingSlashRegExp, '');

function getBreadcrumbSchemaJsonForArticle({ article, edition_data }) {
  return toSchemaJson({
    '@type': 'BreadcrumbList',
    'itemListElement': map(
      article.breadcrumbs,
      ({ url, name } = {}, i) => ({
        '@type': 'ListItem',
        'position': i + 1,
        'item': {
          '@id': stripLeadingSlash(url),
          'url': `https://${edition_data.domain}${url}`,
          'name': name
        }
      })
    )
  });
}

function getImageSchemaJsonForProduct({ product }) {
  return toSchemaJson({
    '@type': 'ImageObject',
    'author': get(product, 'offer.retailer.name'),
    'contentUrl': forceHTTPS(get(product, 'images[0].url')),
    'description': product.description
  });
}

function getProductSchemaJsonForProduct({ product }) {
  const brand = get(product, 'brand.name');
  const description = get(product, 'description');
  const url = forceHTTPS(product.product_url);
  const image = forceHTTPS(get(product, 'images[0].url') || product.thumbnail_url);
  const currency = !isEmpty(get(product, 'offer.currency')) ? get(product, 'offer.currency') : 'USD'; // Default USD
  const price = get(product, 'offer.price');
  const sale_price = get(product, 'offer.sale_price');
  const availability = get(product, 'is_sem3_available');

  return getProductSchema(product.name ?? product.short_name, brand, description, url, image, availability, currency, price, sale_price);
}

function getReviewSchemaJsonForProduct({ product }) {
  const reviews = get(product, 'reviews', []);

  return map(reviews, (review = {}) => toSchemaJson({
    '@type': 'Review',
    'itemReviewed': {
      '@type': 'Product',
      'name': product.name,
      'brand': get(product, 'brand.name'),
      'url': forceHTTPS(product.product_url)
    },
    'reviewBody': review.text,
    'author': {
      '@type': 'Person',
      'name': review.contributor.name
    }
  }));
}

function getOrganizationSchemaJson({ name, url, description, image, brand }) {
  const organizationSchema = {
    '@type': 'Organization',
    'name': name,
    'url': url,
    'description': description,
    'image': {
      '@type': 'ImageObject',
      'author': name,
      'contentUrl': image,
      'description': description
    }
  };

  if (brand) {
    organizationSchema.brand = {
      '@type': 'Brand',
      'name': name,
      'description': description
    };
  }

  return toSchemaJson(organizationSchema);
}

function toSchemaJson(object) {
  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'http://schema.org',
      ...object
    })
  };
}

function getAuthor({ bylines } = {}) {
  const name = get(bylines, '[0].name');
  return name
    ? { '@type': 'Person', name }
    : {};
}

function toISOString(timestamp_s) {
  if (isInteger(timestamp_s)) {
    return new Date(timestamp_s * 1000).toISOString();
  }
}

function getAllSections(article) {
  const body = get(article, 'sections.body', []);
  const slides = get(article, 'sections.slideshow', []);
  return [...body, ...slides];
}

/*
 * Use sparingly. See prepareEmbedCodeFromExternalVideo in js/utils/video/youtube.js
 * for a more bulletproof solution that only works on the client.
 */
function naivelyExtractSrcFromIframeEmbed(iframeEmbed = '') {
  const url = nth(iframeEmbed.match(/src="([^"]+)"/), 1);
  if (url && url.indexOf('//') === 0) {
    return `https:${url}`;
  }

  return url;
}
